 /**
  * SSR-safe timestamp utilities
  * Provides identical timestamps on server and client during hydration
  * to prevent React hydration errors #418/#423
  * 
  * @see src/docs/hydration-fix-json-ld.md
  */
 
 declare global {
   interface Window {
     __SERVER_RENDER_TIME?: number;
   }
 }
 
 /**
  * Get the SSR render timestamp (set before renderToString)
  * Returns null if not in SSR context or if time hasn't been set
  */
 export function getSSRRenderTime(): number | null {
   // Client: use time injected by server
   if (typeof window !== 'undefined' && window.__SERVER_RENDER_TIME) {
     return window.__SERVER_RENDER_TIME;
   }
   // Server: use globalThis (set before render)
   if (typeof globalThis !== 'undefined' && (globalThis as any).__SERVER_RENDER_TIME) {
     return (globalThis as any).__SERVER_RENDER_TIME;
   }
   return null;
 }
 
 /**
  * Get stable ISO date string for SSR
  * Returns empty string if no render time available (safe default for JSON-LD)
  */
 export function getSSRStableNowISO(): string {
   const renderTime = getSSRRenderTime();
   return renderTime ? new Date(renderTime).toISOString() : '';
 }
 
 /**
  * Get stable year for SSR (e.g., for temporalCoverage)
  * Falls back to current year if no render time
  */
 export function getSSRStableYear(): number {
   const renderTime = getSSRRenderTime();
   return renderTime ? new Date(renderTime).getFullYear() : new Date().getFullYear();
 }
 
 /**
  * Get stable validThrough date (future timestamp from SSR time)
  * @param hoursFromNow - Number of hours in the future
  * @returns ISO string of future date, or empty string if no render time
  */
 export function getSSRStableValidThrough(hoursFromNow: number): string {
   const renderTime = getSSRRenderTime();
   if (!renderTime) return '';
   return new Date(renderTime + hoursFromNow * 60 * 60 * 1000).toISOString();
 }
 
 /**
  * Check if we have a valid SSR render time available
  */
 export function hasSSRRenderTime(): boolean {
   return getSSRRenderTime() !== null;
 }